import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGear, faPlus} from "@fortawesome/free-solid-svg-icons";
import React, {CSSProperties, useMemo} from "react";
import {Workflow} from "@/types/workflow";
import Select from "@/Components/Select";
import {isDarkMode, useDarkMode} from "@/hooks/useDarkMode";

type WorkflowSelectProps = {
    onCreateNew: () => void
    onSelect: (id: number|null) => void
    items: Workflow[],
    selectedWorkflowId?: number | null
}

const noneOption = {value: '', label: 'None'};

export default function ({onCreateNew, items = [], selectedWorkflowId, onSelect}: WorkflowSelectProps) {
    const darkMode = useDarkMode();
    const options = useMemo(() => {
        return items.map(wf => ({value: wf.id.toString(), label: wf.name}))
    }, [items])

    const handleOnChange = (workflowId: string) => {
        const workflow = items.find(wf => wf.id.toString() === workflowId);
        onSelect(workflow?.id || null);
    }

    return (
        <Select
            variant="modal"
            isSearchable={false}
            menuPlacement={!items.length ? 'bottom' : 'top'}
            inputId="lang-input"
            onChange={(e) => handleOnChange(e?.value as string)}
            value={
                options.find(o => o.value === selectedWorkflowId?.toString()) ||
                noneOption
            }
            options={[
                ...(options.length ? [noneOption] : []),
                ...options,
                { value: '__create__', label: 'Create New Workflow' }
            ]}
            menuPortalTarget={document ? document.body : undefined}
            styles={{
                control: (provided) => ({
                    ...provided,
                    borderRadius: '5px',
                    'borderColor': darkMode ? '#526380' : '#dadada',
                    'padding': '2px 0px',
                }),
            }}
            components={{
                Option: (props) => {
                    if (props.data.value === '__create__') {
                        return (
                            <div
                                {...props.innerProps}
                                style={{
                                    ...(props.getStyles('option', props) as React.CSSProperties),

                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.selectProps.onMenuClose();
                                    onCreateNew();
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
                                    <span style={{ fontWeight: '500' }}>Create New Workflow</span>
                                </div>
                            </div>
                        );
                    }

                    // For regular options, use the theme styles but apply custom content
                    const customStyle: React.CSSProperties = {
                        ...(props.getStyles('option', props) as React.CSSProperties),
                        ...(props.data.value === '' && {
                            fontStyle: 'italic',
                            color: '#9CA3AF',
                        })
                    };

                    return (
                        <div {...props.innerProps} style={customStyle}>
                            {props.children}
                            {props.isSelected && (
                                <span style={{
                                    color: '#60A5FA',
                                    fontWeight: '600',
                                    fontSize: '16px',
                                    marginLeft: 'auto'
                                }}>
                        ✓
                    </span>
                            )}
                        </div>
                    );
                },
                IndicatorSeparator: () => null,
                SingleValue: (props) => (
                    <div style={props.getStyles('singleValue', props) as React.CSSProperties} {...props.innerProps}>
                        <FontAwesomeIcon icon={faGear} className="text-gray-400 mr-2" size="sm"/>
                        <span>
                {props.children}
            </span>
                    </div>
                )
            }}
        />
    )
}
